<!-- TODO: Replace video -->

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { gsap } from "gsap";
import { useMediaQuery } from "@vueuse/core";
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import { useGlobalStore } from "@stores/global";
import { useExpediaStore } from "@stores/expedia";
import { Swiper, SwiperSlide } from "swiper/vue";
import { FreeMode, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import IconPin from "@components/icons/Pin.vue";
import IconX from "@components/icons/X.vue";

const globalStore = useGlobalStore();
const expediaStore = useExpediaStore();
const isMobile = useMediaQuery("(max-width: 1024px)");

const open = computed(() => globalStore.modal.name === "exploreIntro");

const featuredVideo = computed(() => globalStore.featuredVideos[0]);

// Use the mobile cut when on a small screen (falls back to the desktop source if
// no mobile clip is set). `isMobile` is reactive, so this swaps live on resize.
const backgroundVideo = computed(() =>
    isMobile.value
        ? (featuredVideo.value?.video_mobile ?? featuredVideo.value?.video)
        : featuredVideo.value?.video,
);
const backgroundPoster = computed(() =>
    isMobile.value
        ? (featuredVideo.value?.poster_mobile ?? featuredVideo.value?.poster)
        : featuredVideo.value?.poster,
);

const currentTime = ref(0);

function onTimeUpdate(e) {
    currentTime.value = e.target.currentTime;
}

// All activities whose window contains the current playhead.
const activeSet = computed(() =>
    (featuredVideo.value?.activities ?? []).filter(
        (activity) =>
            currentTime.value >= activity.timestamp_start_at &&
            currentTime.value <= activity.timestamp_end_at,
    ),
);

// Stable identifier for the current set so we only react when it truly changes
// (not on every timeupdate tick).
const activeSetKey = computed(() =>
    activeSet.value.map((activity) => activity.guide_url).join("|"),
);

// The set we actually render; updated only when the window changes so GSAP can
// animate the swap.
const activities = ref([]);

const swiperRef = ref(null);
let setTween;

watch(activeSetKey, async () => {
    activities.value = activeSet.value;
    await nextTick();

    const root = swiperRef.value?.$el ?? swiperRef.value;
    const cards = root?.querySelectorAll(".intro-card");
    if (!cards?.length) return;

    setTween?.kill();
    setTween = gsap.fromTo(
        cards,
        { autoAlpha: 0, y: 16 },
        {
            autoAlpha: 1,
            y: 0,
            duration: 0.4,
            ease: "power2.out",
            stagger: 0.08,
            overwrite: true,
        },
    );
});

const cardsVisible = ref(true);

function onHeaderEnter(el, done) {
    gsap.fromTo(
        el,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.3, ease: "power2.out", onComplete: done },
    );
}

function onHeaderLeave(el, done) {
    gsap.to(el, {
        autoAlpha: 0,
        duration: 0.3,
        ease: "power2.out",
        onComplete: done,
    });
}

function close() {
    globalStore.markExploreIntroSeen();
    globalStore.closeModal();
}

const trapRef = ref(null);
const { activate, deactivate } = useFocusTrap(trapRef, {
    allowOutsideClick: true,
    escapeDeactivates: false,
});

function onKey(e) {
    if (e.key === "Escape") close();
}

watch(open, async (isOpen) => {
    if (isOpen) {
        globalStore.loadFeaturedVideos();
        await nextTick();
        activate();
        window.addEventListener("keydown", onKey);
    } else {
        deactivate();
        window.removeEventListener("keydown", onKey);
    }
});

onBeforeUnmount(() => {
    setTween?.kill();
    window.removeEventListener("keydown", onKey);
});
</script>

<template>
    <Teleport to="body">
        <Transition name="modal">
            <div
                v-if="open"
                class="fixed inset-0 z-50 flex items-center justify-center"
            >
                <div
                    ref="trapRef"
                    class="bg-marine-900 relative h-svh w-screen overflow-hidden"
                >
                    <video
                        v-if="backgroundVideo"
                        :key="backgroundVideo"
                        :src="backgroundVideo"
                        :poster="backgroundPoster"
                        class="absolute inset-0 size-full object-cover"
                        controls
                        autoplay
                        muted
                        loop
                        playsinline
                        @timeupdate="onTimeUpdate"
                    ></video>

                    <div class="absolute top-4 right-4 size-12">
                        <button
                            class="text-marine-800 border-marine-600 flex size-12 items-center justify-center rounded-md border-3 bg-yellow-400 shadow-[0_6px_0_0_var(--color-marine-600)] transition-transform active:translate-y-1 active:shadow-[0_2px_0_0_var(--color-marine-600)]"
                            @click="close"
                        >
                            <span class="sr-only">Close</span>
                            <IconX class="size-4.5" />
                        </button>
                    </div>

                    <div
                        class="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center gap-3.5 px-3 pt-6 pb-22 lg:px-6 lg:pb-24"
                    >
                        <Transition name="cards">
                            <div
                                v-if="cardsVisible"
                                class="mx-auto flex w-full max-w-[1200px] flex-col gap-3.5 md:overflow-hidden"
                            >
                                <Transition
                                    :css="false"
                                    @enter="onHeaderEnter"
                                    @leave="onHeaderLeave"
                                >
                                    <div
                                        v-if="activities.length"
                                        class="pointer-events-auto flex w-full items-center justify-between"
                                    >
                                        <p
                                            class="text-xs leading-[0.9] font-bold tracking-tight text-white/50 uppercase"
                                        >
                                            Shop the Shoot
                                        </p>
                                        <button
                                            class="text-xs leading-none font-extrabold tracking-tight text-yellow-400 uppercase transition-opacity hover:opacity-70"
                                            @click="
                                                close();
                                                globalStore.setPage(
                                                    'activities',
                                                );
                                            "
                                        >
                                            See all Activities
                                        </button>
                                    </div>
                                </Transition>

                                <Swiper
                                    ref="swiperRef"
                                    :modules="[FreeMode, Mousewheel]"
                                    :slides-per-view="2"
                                    :space-between="8"
                                    :breakpoints="{
                                        768: {
                                            slidesPerView: 3,
                                            spaceBetween: 11,
                                        },
                                        1024: {
                                            slidesPerView: 5,
                                            spaceBetween: 11,
                                        },
                                    }"
                                    :free-mode="true"
                                    :mousewheel="{ forceToAxis: true }"
                                    :grab-cursor="true"
                                    class="intro-swiper pointer-events-auto w-full"
                                >
                                    <SwiperSlide
                                        v-for="activity in activities"
                                        :key="activity.guide_url"
                                    >
                                        <a
                                            :href="
                                                expediaStore.href(
                                                    activity.guide_url,
                                                )
                                            "
                                            target="_blank"
                                            rel="noopener"
                                            class="intro-card flex items-center overflow-hidden bg-black/20 backdrop-blur-xl transition-opacity hover:bg-black/30 lg:h-50 lg:flex-col lg:items-stretch"
                                        >
                                            <div
                                                class="h-16 w-20 shrink-0 overflow-hidden bg-black/40 lg:h-auto lg:w-full lg:flex-1"
                                            >
                                                <img
                                                    :src="activity.guide_image"
                                                    alt=""
                                                    class="size-full object-cover"
                                                />
                                            </div>
                                            <div
                                                class="flex min-w-0 flex-1 items-center p-3 lg:h-16 lg:flex-none"
                                            >
                                                <div
                                                    class="flex min-w-0 flex-1 flex-col gap-2"
                                                >
                                                    <p
                                                        class="body-md truncate leading-[0.9] font-bold tracking-tight text-white uppercase"
                                                    >
                                                        {{ activity.name }}
                                                    </p>
                                                    <div
                                                        class="flex items-center gap-1"
                                                    >
                                                        <IconPin
                                                            class="size-3 shrink-0 text-yellow-400"
                                                        />
                                                        <p
                                                            class="body-xs leading-[0.9] font-medium tracking-tight text-white/50 uppercase"
                                                        >
                                                            {{
                                                                activity.city
                                                                    .name
                                                            }},
                                                            {{
                                                                activity.city
                                                                    .country
                                                                    .name
                                                            }}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </a>
                                    </SwiperSlide>
                                </Swiper>
                            </div>
                        </Transition>

                        <Transition name="cards">
                            <button
                                v-if="activeSet.length"
                                class="pointer-events-auto flex items-center gap-2 text-xs leading-none font-extrabold tracking-tight text-white uppercase transition-opacity hover:opacity-70"
                                @click="cardsVisible = !cardsVisible"
                            >
                                {{ cardsVisible ? "Hide cards" : "Show cards" }}
                                <svg
                                    class="size-3.5 transition-transform"
                                    :class="cardsVisible ? '' : 'rotate-180'"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                >
                                    <path
                                        d="M4 6l4 4 4-4"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="square"
                                    />
                                </svg>
                            </button>
                        </Transition>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.intro-swiper {
    width: 100%;
    overflow: visible;
}

.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.cards-enter-active,
.cards-leave-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
}
.cards-enter-from,
.cards-leave-to {
    opacity: 0;
    transform: translateY(12px);
}
</style>
